import { Model, ModelOrigin } from 'src/app/interfaces/api/model';
import { ZoneColor, ZoneType } from 'src/app/interfaces/api/zone';
import { Config } from 'src/app/interfaces/shared/config';
import { createExtendedMesh } from 'src/app/interfaces/shared/extendMesh';
import { XYZ } from 'src/app/interfaces/shared/xyz';
import * as Tools from '../../shared/threeDTools';

export class ZoneShape {
    polygon: number[][];
    z: number;
    height: number;
    name: string;
    number: number;
    color: "danger" | "warning" | "primary" | "success" | "info" | "dark" | "muted";
    alpha: number;
    onAfterUpdate?: () => void;
    // mesh: ExtendedMesh;
    mesh: any;
    type: "sphere" | "circle" | "plane" | "box" | "polygon" | "rectangle" | "geometry";
    geoMesh: THREE.Mesh | null = null;
    shape!: number[][];
    id: string;
    overlay: string;
    visible: boolean;
    origin: ModelOrigin;
    projectXY: [number, number];
    center: { x: number; y: number; z: number };
    previousColor: ZoneColor | undefined;
    offset = {
        x: 0,
        y: 0,
        z: 0
    };
    colors = Config.colors;
    viewer: any;
    geometry?: any;
    position: {
        lat?: number;
        lon?: number;
        x: number;
        y: number;
        z: number;
    } = { x: 0, y: 0, z: 0 }
    rotation: XYZ = { x: 0, y: 0, z: 0 };
    enable!: boolean;
    createdDate!: number | string;

    constructor(
        params: {
            viewer: any;
            model: Model;
            polygon: number[][];
            type: ZoneType;
            z: number;
            height: number;
            name: string;
            number: number;
            color: ZoneColor;
            alpha?: number;
            onAfterUpdate?: () => void;
            id: string;
            visible: boolean;
            center?: { x: number; y: number; z: number };
            geometry?: any;
        }) {
        this.viewer = params.viewer;
        this.origin = params.model.origin;

        if (this.viewer) {
            let offset = this.viewer.model.getData().globalOffset;
            if (offset)
                this.offset = offset;
        }

        this.projectXY = params.model.origin.projectXY || [0, 0];
        this.polygon = params.polygon; // list of points
        this.type = params.type;
        this.z = params.z;
        this.height = params.height;
        this.name = params.name;
        this.number = params.number;
        this.color = params.color;
        this.alpha = params.alpha || 0.5;
        this.onAfterUpdate = params.onAfterUpdate;
        this.mesh = createExtendedMesh();
        this.id = params.id;
        this.overlay = `zoneOverlay${params.number}`;
        this.visible = params.visible;
        this.center = params.center || { x: 0, y: 0, z: 0 };
        this.geometry = params.geometry;

    }

    toJSON(): {
        id: string;
        name: string;
        number: number;
        visible: boolean;
        color: string;
        alpha: number;
        z: number;
        height: number;
        polygon: number[][];
    } {

        return {
            id: this.id,
            name: this.name,
            number: this.number,
            visible: this.visible,
            color: this.color,
            alpha: this.alpha,
            z: this.z,
            height: this.height,
            polygon: this.polygon
        }
    }
    updateOrigin(): void {

        this.mesh.updateMatrix();

        let geometry = new THREE.Geometry();

        this.polygon.forEach(v => {
            geometry.vertices.push(
                new THREE.Vector3(v[0], v[1], 0)
            );
        })
        geometry.applyMatrix(this.mesh.matrix)

        let vertices = geometry.vertices;

        let array: any[] = [];

        vertices.forEach(v => {
            array.push([v.x, v.y])
        })

        this.polygon = array;
    }

    getLocalPoints(): number[][] {
        // Convert polygon coords to lat/lon, then offset from model center.
        // applyTransform not used (removes globalOffset for Forge CRS).
        const originLat = this.origin?.position?.lat || this.origin?.position?.mapLat || 0;
        const originLon = this.origin?.position?.lon || this.origin?.position?.mapLon || 0;
        const scale = this.origin?.scale || 1;
        const angle = this.origin?.zAngle || 0;
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);
        const result = [];

        for (let i = 0; i < this.polygon.length; i++) {
            const px = this.polygon[i]?.[0] || 0;
            const py = this.polygon[i]?.[1] || 0;
            const latLon = Tools.getLatLonFromXYZ(px, py, 0);
            const lon = latLon[0] ?? 0;
            const lat = latLon[1] ?? 0;
            const relXY = Tools.getXYFromLatLonAlt2(lat, lon, 0, originLat, originLon);
            const rx = (relXY[0] ?? 0) * cosA - (relXY[1] ?? 0) * sinA;
            const ry = (relXY[0] ?? 0) * sinA + (relXY[1] ?? 0) * cosA;

            result.push([
                rx * scale - (this.origin?.projectXY?.[0] ?? 0),
                ry * scale - (this.origin?.projectXY?.[1] ?? 0)
            ]);
        }

        return result;
    }


    getTransformedPoints(): number[][] {
        let pointTransformed = [];

        for (var i = 0; i < this.polygon.length; i++) {

            let point = Tools.reverseTransform({ x: this.polygon[i]?.[0] || 0, y: this.polygon[i]?.[1] || 0, z: 0 }, this.origin!, this.offset);

            pointTransformed.push([
                point.x,
                point.y
            ]);
        }

        return pointTransformed.slice();
    }

    getLocalGeometry(): any {
        let pointLocal = [];
        let geometry = { ...this.geometry };

        for (var i = 0; i < this.geometry!.position.length; i += 3) {
            const px = this.geometry!.position[i];
            const py = this.geometry!.position[i + 1];
            const pz = this.geometry!.position[i + 2];
            const latLon = Tools.getLatLonFromXYZ(px, py, 0);
            
            const point = Tools.applyTransform(
                { lat: latLon[1]!, lon: latLon[0]!, z: pz } as any,
                this.origin!,
                this.offset
            );

            pointLocal.push(point.x, point.y, point.z);
        }

        geometry.position = pointLocal;
        return geometry;
    }

    getTransformedGeometry(): any {

        let pointTransformed = [];
        let geometry = { ...this.geometry };

        for (var i = 0; i < this.geometry!.position.length; i += 3) {
            let vec = [this.geometry!.position[i], this.geometry!.position[i + 1], this.geometry!.position[i + 2]];
            let point = Tools.reverseTransform({ x: vec[0], y: vec[1], z: vec[2] }, this.origin!, this.offset);
            pointTransformed.push(point.x)
            pointTransformed.push(point.y)
            pointTransformed.push(point.z)
        }

        geometry.position = pointTransformed;
        return geometry;
    }

    redraw(origin?: ModelOrigin): void {
        if (origin)
            this.origin = origin;

        if (this.mesh) {
            this.remove();
            this.draw()
        }

        if (this.geoMesh) {
            this.removeGeom();
            this.drawGeometry()
        }
    }

    draw(transform: boolean = true): void {
        if (this.mesh && this.mesh.parent) {
            this.viewer.impl.removeOverlay("zone_" + this.previousColor, this.mesh);
        }

        var shape = new THREE.Shape();

        if (!this.polygon.length) return;

        let polygon = transform ? this.getLocalPoints() : this.polygon;
        shape.moveTo(polygon[0]?.[0] || 0, polygon[0]?.[1] || 0);

        for (var i = 1; i < polygon.length; i++) {
            shape.lineTo(polygon[i]?.[0] || 0, polygon[i]?.[1] || 0);
        }

        shape.lineTo(polygon[0]?.[0] || 0, polygon[0]?.[1] || 0);

        var extrudeSettings = {
            steps: 2,
            amount: transform ? this.height * (this.origin?.scale || 1) : this.height,
            bevelEnabled: false,
            bevelThickness: 1 * (this.origin?.scale || 1),
            bevelSize: 1 * (this.origin?.scale || 1),
            bevelSegments: 1
        };

        var material = new THREE.MeshBasicMaterial({ color: this.colors[this.color], transparent: true, opacity: this.alpha, depthWrite: false });
        var geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.name = this.name + "_bbox";

        let z = (this.z - (this.origin?.position?.z || 0)) * (this.origin?.scale || 1);
        this.mesh.position.setZ(transform ? z : this.z);
        this.mesh.visible = this.visible;

        if (!transform) {
            this.mesh.geometry.computeBoundingBox()
            this.mesh.geometry.center();
            let newBBox = this.mesh.geometry.boundingBox;

            if (newBBox) {
                let newPolygon = [
                    [newBBox.max.x, newBBox.max.y],
                    [newBBox.max.x, newBBox.min.y],
                    [newBBox.min.x, newBBox.min.y],
                    [newBBox.min.x, newBBox.max.y]
                ];
                this.polygon = newPolygon;
            }

            let offsetMatrix = new THREE.Matrix4();
            offsetMatrix.setPosition(new THREE.Vector3(this.center.x, this.center.y, this.center.z));
            this.mesh.applyMatrix4(offsetMatrix);
        }

        this.previousColor = this.color;
        const overlayKey = "zone_" + this.id;
        this.viewer.impl.createOverlayScene(overlayKey);
        this.viewer.impl.addOverlay(overlayKey, this.mesh);
        this.viewer.impl.invalidate(true, true, true);
        this.viewer.impl.sceneUpdated(true);
    }

    drawGeometry(transform: boolean = true): void {
        this.viewer.impl.removeOverlay("zone_" + this.id, this.geoMesh);

        //draw geometry
        if (this.geometry) {
            let localGeometry = transform ? this.getLocalGeometry() : this.geometry;
            var bGeometry = new THREE.BufferGeometry();

            bGeometry.addAttribute("position", new THREE.BufferAttribute(new Float32Array(localGeometry.position), 3));
            bGeometry.addAttribute("normal", new THREE.BufferAttribute(new Float32Array(localGeometry.normal), 3));

            var bMaterial = new THREE.MeshBasicMaterial({ color: this.colors[this.color], transparent: true, opacity: 1, depthWrite: false });
            this.geoMesh = new THREE.Mesh(bGeometry, bMaterial);

            this.geoMesh.name = this.name + "_geometry";
            this.geoMesh.visible = this.visible;
            this.viewer.impl.addOverlay("zone_" + this.id, this.geoMesh);
        }

        this.viewer.impl.invalidate(true, true, true);
    }

    getMesh(transform: boolean = true): THREE.Mesh | null {
        //draw geometry
        if (this.geometry) {
            let localGeometry = transform ? this.getLocalGeometry() : this.geometry;
            var bGeometry = new THREE.BufferGeometry();
            bGeometry.addAttribute("position", new THREE.BufferAttribute(new Float32Array(localGeometry.position), 3));
            bGeometry.addAttribute("normal", new THREE.BufferAttribute(new Float32Array(localGeometry.normal), 3));

            var bMaterial = new THREE.MeshBasicMaterial({ color: this.colors[this.color], transparent: true, opacity: 1, depthWrite: false });
            this.geoMesh = new THREE.Mesh(bGeometry, bMaterial);
            // this.geoMesh.position.setZ(transform ? this.z * this.origin.scale : this.z);
            this.geoMesh.name = this.name + "_geometry";
            this.geoMesh.visible = this.visible;
            return this.geoMesh;
        } else {
            return null
        }

    }

    remove(): void {
        if (this.mesh) {
            this.mesh.visible = false;
            this.viewer.impl.removeOverlay("zone_" + this.id, this.mesh);
            this.viewer.impl.invalidate(true, true, true);
        }
    };

    removeGeom(): void {
        if (this.geoMesh) {
            this.geoMesh.visible = false;
            this.viewer.impl.removeOverlay("zone_" + this.id, this.geoMesh);
            this.viewer.impl.invalidate(true, true, true);
        }
    };
}